import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {
    DialogActionsEnum,
    DialogComponent,
    DialogConfigInterface,
    Pagination,
    SelectOptionInterface
} from 'appShared/components';
import {FormInterface, PermissionRuleInterface} from 'appShared/interfaces';
import {Subscription} from 'rxjs';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
    ApiError,
    ApiResponse,
    BrokerSearchInputModel, BrokerSearchOutputInterface,
    BrokerSearchOutputItemModel,
    BrokerSearchOutputModel,
    HttpStatusCodesEnum,
    OrderRouteService, RouteGetOutputModel,
    ServerResponseData
} from '@spa/api';
import {Router} from '@angular/router';
import {AlertsService} from 'appShared/services';
import {PermissionRulesTrait} from 'appEntries/panel/traits';
import {AlertTypesEnum, PermissionRuleCheckStrategiesEnum} from 'appShared/enums';
import {FormComponent} from 'appShared/widgets';
import {PanelOrderRoutesMessages} from 'appAssets/messages';
import {OrderRoutesFormErrorStatesEnum, PanelOrderRoutesResolverErrorStatesEnum} from 'appEntries/panel/enums';
import {RouteForm} from 'appEntries/panel/forms';
import {Helper, SelectControl} from 'appShared/classes';
import {EventsService} from 'appEntries/panel/services';

@Component({
    selector: 'app-add-route',
    templateUrl: './add-route.component.html',
    styleUrls: ['./add-route.component.scss']
})
export class AddRouteComponent extends DialogComponent<AddRouteComponent> implements FormInterface, OnDestroy, OnInit {

    protected subscriptions: Subscription[];
    public permissionRule: PermissionRuleInterface;
    public form: RouteForm;
    public hideBroker: boolean;
    public brokerId: number;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            config: DialogConfigInterface,
            brokerId: number,
            hideBroker: boolean,
        },
        protected router: Router,
        protected alertsService: AlertsService,
        protected orderRouteService: OrderRouteService,
        protected eventService: EventsService,
        public dialogRef: MatDialogRef<AddRouteComponent>,
    ) {
        super(dialogRef);
        this.subscriptions = [];
        this.permissionRule = PermissionRulesTrait.getOrderRoutesCreatePermissionRule(PermissionRuleCheckStrategiesEnum.Union);
        this.config = data.config;
        this.brokerId = data.brokerId;
        this.hideBroker = data.hideBroker;
    }

    private LoadBrokers(brokerFilter: BrokerSearchInputModel) {
        this.orderRouteService.searchBrokers(brokerFilter.serialize()).subscribe(response => {
            if (response.status !== HttpStatusCodesEnum.Ok) {
                this.stopLoading();

                return Helper.errorsHelper().createErrorResponse<BrokerSearchOutputInterface>(
                    response,
                    PanelOrderRoutesResolverErrorStatesEnum.GetBrokerSearchExtendedFailed
                );
            }
            const result = new BrokerSearchOutputModel(response.data as BrokerSearchOutputInterface);
            const selectControl = this.form.controls.broker as SelectControl;
            selectControl.options = result.content.map(item =>
                ({value: item.getId(), label: item.getName()} as SelectOptionInterface));
            const formControl = this.form.formGroup.get(selectControl.name);
            formControl.patchValue(this.brokerId ?? (result.total_elements === 1 ? result.content[0].getId() : null));
            this.stopLoading();
        });
    }

    handleErrorResponse(response: ApiResponse<ApiError[] | RouteGetOutputModel>, error: ApiError): string {
        if (Array.isArray(response.messages) && response.messages[0]) {
            return response.messages[0];
        }

        return this.getErrorMessageByApiError(error);
    }

    onFormSubmit(action?: number): void {
        if (action !== DialogActionsEnum.Confirm) {
            this.dialogRef.close(false);

            return;
        }

        if (this.validateForm()) {
            const requestSubscription = this.form.createRequest()
                .subscribe((response: ServerResponseData<RouteGetOutputModel>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        (response.data as ApiError[]).map((error: ApiError) => this.handleErrorResponse(response, error))
                            .filter((errorMessage: string) => {
                                return errorMessage.trim();
                            }).forEach((errorMessage: string) => {
                            this.alertsService.addAlert(errorMessage);
                        });

                        return;
                    }
                    this.alertsService.addAlert(this.getSuccessFormMessage(), AlertTypesEnum.Success);
                    const result = response.data as RouteGetOutputModel;
                    this.eventService.forceExecutionRouteGet.next({route: result});
                    this.dialogRef.close(true);

                });
            this.subscriptions.push(requestSubscription);
        }
    }

    getFormMessages(controlLabel?: string): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel);
    }

    validateForm(): boolean {
        return this.form.validateForm(this.getFormMessages);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription: Subscription) => subscription && subscription.unsubscribe());
    }

    ngOnInit(): void {
        this.initForm();

        const brokerFilter = new BrokerSearchInputModel(
            Pagination.defaultPagesChunk,
            Pagination.maxInt32PerPage,
            BrokerSearchOutputItemModel.getNameSelector(),
            false,
            null,
            null,
            null,
            null,
            null,
            null
        );
        this.dialogRef.afterOpened().subscribe(() => {
            this.startLoading(() => {
                if (this.brokerId != null && this.hideBroker) {
                    const selectControl = this.form.controls.broker as SelectControl;
                    selectControl.options = [{value: this.brokerId, label: this.brokerId.toString()}];
                    const formControl = this.form.formGroup.get(selectControl.name);
                    formControl.patchValue(this.brokerId);
                    this.stopLoading();
                } else {
                    this.LoadBrokers(brokerFilter);
                }

            });

        });

    }

    initForm(): void {
        this.form = new RouteForm(this.orderRouteService);
        this.form.initForm();
    }

    getSuccessFormMessage(): string {
        return PanelOrderRoutesMessages.RouteCreationSucceed.message;
    }

    getErrorMessageByApiError(error: ApiError): string {
        if (error.status === OrderRoutesFormErrorStatesEnum.CreateRoutesFailed) {
            return PanelOrderRoutesMessages.RouteCreationFailed.message;
        }

        return PanelOrderRoutesMessages.UnknownError.message;
    }
}
